import type { MarketingRouterSignificance } from '@luxuryescapes/contract-svc-promo/marketing-router/admin';

import type { MarketingRouterRule } from '~/types/marketingRouterRule';

import { getComparableMetrics } from './marketingRouterComparison';
import { toMatchValues } from './marketingRouterMatch';
import {
  type MarketingRouterMetricKey,
  summariseVariantVisitors,
  totalVisitors,
} from './marketingRouterSignificanceDisplay';

/**
 * Pure display logic for comparing one rule's results campaign by campaign.
 *
 * Each campaign is its own request to svc-promo, filtered to the visitors whose
 * first assigned session came from that campaign. Every visitor is attributed
 * to exactly one campaign, so the blocks never share visitors — but nothing
 * here pools them either: every number is one svc-promo reported for that
 * campaign, against that campaign's own control.
 */

/**
 * Above this the side-by-side table stops fitting on a screen, and each extra
 * campaign is another full significance query against the warehouse.
 */
export const MAX_COMPARED_CAMPAIGNS = 4;

export type CampaignComparisonEntry = {
  campaign: string;
  /** Undefined while the campaign's results are still loading, or failed to load. */
  significance?: MarketingRouterSignificance;
};

/**
 * svc-promo matches campaigns case-insensitively against a lower-cased export,
 * so "Spring" and "spring" are the same campaign. Normalising here keeps one
 * campaign to one option, one column and one cache entry.
 */
export function normalizeCampaign(campaign: string): string {
  return campaign.trim().toLowerCase();
}

/**
 * svc-promo reads `utmCampaigns` as a comma-separated list, so a comma inside
 * one campaign would silently become several campaigns pooled into one block.
 */
const CAMPAIGN_SEPARATOR = ',';

/**
 * The campaigns a selection holds, normalised, de-duplicated and capped, in
 * the order they were picked — which is the order of the blocks on screen.
 * Typed text holding commas ("spring,winter") becomes one campaign per part,
 * so every block is exactly one campaign svc-promo filters on.
 */
export function toCampaignSelection(campaigns: Array<string>): Array<string> {
  const normalized = campaigns
    .flatMap((campaign) => campaign.split(CAMPAIGN_SEPARATOR))
    .map(normalizeCampaign)
    .filter((campaign) => campaign !== '');

  return [...new Set(normalized)].slice(0, MAX_COMPARED_CAMPAIGNS);
}

/**
 * The campaigns the rule targets, offered as the default choices. A rule with
 * no campaign constraint matches every campaign, so it offers none and the
 * operator types the ones they want to compare.
 */
export function getRuleCampaignOptions(match: MarketingRouterRule['match']): Array<string> {
  return [...new Set(toMatchValues(match.utm_campaign).map(normalizeCampaign))].filter(
    // A campaign holding a comma cannot be sent to svc-promo as one campaign,
    // so it is not offered rather than offered with the wrong results.
    (campaign) => campaign !== '' && !campaign.includes(CAMPAIGN_SEPARATOR),
  );
}

/** Every visitor svc-promo counted for this campaign, across all variants. */
export function getCampaignVisitors(significance: MarketingRouterSignificance): number {
  return totalVisitors(
    summariseVariantVisitors(significance.results, significance.baseVariantId, significance.baseVariantName),
  );
}

/**
 * What the comparison opens on: the rule's primary metric when any campaign
 * reports it. Read off any campaign, not just the first — the first campaign
 * may have no traffic yet while the others do.
 */
export function getDefaultCampaignMetric(
  entries: Array<CampaignComparisonEntry>,
  primaryMetric: MarketingRouterMetricKey | undefined,
): MarketingRouterMetricKey | undefined {
  const metrics = getComparableMetrics(entries);

  return primaryMetric !== undefined && metrics.includes(primaryMetric) ? primaryMetric : metrics[0];
}

/**
 * A campaign svc-promo says failed its sample-ratio check: its traffic is not
 * splitting between variants the way the rule is configured. A campaign still
 * loading has no verdict yet, so it is not called unreliable.
 */
export function isCampaignUnreliable(entry: CampaignComparisonEntry): boolean {
  return entry.significance !== undefined && !entry.significance.health.srm.healthy;
}

/** The campaigns whose numbers cannot be trusted. */
export function getUnreliableCampaigns(entries: Array<CampaignComparisonEntry>): Array<string> {
  return entries.filter(isCampaignUnreliable).map((entry) => entry.campaign);
}
